import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFirebaseApp } from '@goschool/react-firebase'
import { getDownloadURL, getStorage, ref } from 'firebase/storage'
import { Document, Page, pdfjs, Thumbnail } from 'react-pdf'
import { alpha, Stack, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import type { PDFDocumentProxy } from 'pdfjs-dist'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

export function PdfViewer({
  storageRef,
  onPageChange,
}: {
  storageRef: string | undefined
  onPageChange?: (pageNumber: number) => void
}) {
  const app = useFirebaseApp()
  const { t } = useTranslation()
  const [pdfUrl, setPdfUrl] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    const storage = getStorage(app)
    if (storageRef != null) {
      let cancelled = false
      setLoading(true)
      setError(undefined)
      setPdf(undefined)
      setPageNumber(1)
      const pdfRef = ref(storage, storageRef)
      getDownloadURL(pdfRef)
        .then((url) => {
          if (cancelled) return
          setPdfUrl(url)
          setLoading(false)
        })
        .catch((_err) => {
          if (cancelled) return
          setError(t('document:viewer.errors.load'))
          setLoading(false)
        })
      return () => {
        cancelled = true
      }
    } else {
      setPdfUrl(undefined)
      setError(t('document:viewer.errors.noPdf'))
      setLoading(false)
    }
  }, [storageRef, app, t])

  const [pageNumber, setPageNumber] = useState<number>(1)
  const [pdf, setPdf] = useState<PDFDocumentProxy>()
  const numPages = useMemo(() => pdf?.numPages, [pdf])
  const pageViewRef = useRef<HTMLDivElement | null>(null)
  const [pageWidth, setPageWidth] = useState<number>(720)

  const onDocumentLoadSuccess = useCallback((pdf: PDFDocumentProxy) => {
    setPdf(pdf)
  }, [])

  const onItemClick = useCallback(({ pageNumber }: { pageNumber: number }) => {
    setPageNumber(pageNumber)
  }, [])

  useEffect(() => {
    onPageChange?.(pageNumber)
  }, [pageNumber, onPageChange])

  useEffect(() => {
    const element = pageViewRef.current
    if (element == null) return

    const updateWidth = () => {
      // Account for container padding and avoid too small renders.
      const nextWidth = Math.max(320, Math.floor(element.clientWidth - 24))
      setPageWidth((current) => (current !== nextWidth ? nextWidth : current))
    }

    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const onDocumentError = useCallback(
    (_err: unknown) => {
      setError(t('document:viewer.errors.render'))
    },
    [t]
  )

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: 400 }}>
        <span>{t('document:viewer.loading')}</span>
      </Stack>
    )
  }
  if (error) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: 400, color: 'red' }}
      >
        <span>{error}</span>
      </Stack>
    )
  }
  if (!pdfUrl) {
    return null
  }

  return (
    <Stack
      direction="column"
      overflow="hidden"
      alignItems="center"
      justifyContent="flex-start"
      width="100%"
    >
      {/*<Pagination disabled={numPages==null} count={numPages} page={pageNumber}*/}
      {/*            siblingCount={2} boundaryCount={2}*/}
      {/*            onChange={(event, value) => setPageNumber(value)} />*/}

      <Document
        file={pdfUrl}
        onItemClick={onItemClick}
        onLoadSuccess={onDocumentLoadSuccess}
        onError={onDocumentError}
      >
        <Stack
          sx={{ height: '800px', width: '100%' }}
          direction="row"
          alignItems="stretch"
          justifyContent="stretch"
        >
          <Toc>
            {Array.from(new Array(numPages), (_el, index) => (
              <Thumbnail
                key={`thumbnail_${index + 1}`}
                className={pageNumber === index + 1 ? 'selected' : ''}
                pageNumber={index + 1}
                width={100}
              >
                <PageNumber>
                  <span>{index + 1}</span>
                </PageNumber>
              </Thumbnail>
            ))}
          </Toc>
          <PageView ref={pageViewRef}>
            <Page pageNumber={pageNumber ?? 1} width={pageWidth} />
          </PageView>
        </Stack>
      </Document>
    </Stack>
  )
}

const Toc = styled('div', { name: 'Toc', slot: 'Root' })(({ theme }) => ({
  flex: '0 0 120px',
  overflowY: 'scroll',
  borderRight: `1px solid ${theme.palette.divider}`,
  '& .react-pdf__Thumbnail': {
    '&:hover': {
      '& .css-k620w6-PageNumber-root span': {
        boxShadow: `0 0 15px ${alpha(theme.palette.divider, 0.5)}`,
      },
    },
    '&.selected': {
      '& .css-k620w6-PageNumber-root span': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },
    '& .react-pdf__Thumbnail__page': {
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}))

const PageNumber = styled('div', { name: 'PageNumber', slot: 'Root' })(
  ({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary,
    '& span': {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 10px ${alpha(theme.palette.divider, 0.5)}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 'bold',
    },
  })
)

const PageView = styled('div', { name: 'PageView', slot: 'Root' })(
  ({ theme }) => ({
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    '& .react-pdf__Page': {
      margin: theme.spacing(1),
      boxShadow: theme.shadows[3],
    },
    '& .react-pdf__Page canvas': {
      maxWidth: '100%',
      height: 'auto !important',
    },
  })
)
